import React, { useCallback, useContext, useMemo } from 'react';
import ImportDialog, {
  IConvertedClipboardValue,
  IInternalSelectableColumnHeader,
  ISelectableColumnHeader,
} from '../../../../../Ekonomi/ObjektVyer/Bokforder/Components/ImportDialog/ImportDialog';
import { KonteringContext } from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { KONTERING_COLLECTION_NAME } from '../Kontering/KonteringsForslagComp';
import shortid from 'shortid';
import { TEDLRLeverantorfakturaMOMSHANTERINGEnum } from '../../../../../../../globalQueryTypes';
import {
  getKonteringRadtyp,
  isNumberWithinMarginOfError,
} from '../../Utils/FakturadelUtils';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../FakturaDelContextManager/FakturaDelContextManager';
import {
  getUtlandskAvdrag,
  getUtlandskMoms,
  roundToTwoDecimals,
} from '../Kontering/Utils/KonteringUtils';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import {
  currencyValueFormatter,
  isNumberCellValueValid,
} from '../../../../../Ekonomi/ObjektVyer/Bokforder/Components/ImportDialog/Utils/importDialogUtils';
import { FakturaattestWrapperContext } from '../../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { LR_MOMSHANTERING_LEVFAKT_MOMSATSTOLERANS } from '../../Consts/FakturaDelSysoptionConsts';
import { MOMSSATS_TOLERANS_DEFAULT_VALUE } from '../../Consts/FakturadelConsts';
import { INewKonteringValues } from '../FakturaAttestKonteringHistoryModal/FakturaAttestKonteringHistoryModal';
import {
  getCalculatedValueObject,
  getKonteringsforslagValues,
} from '../FakturaAttestKonteringHistoryModal/Utils/KonteringHistoryModalUtils';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';

const currFormatter = findFormatter('TEDFormatCurr');

interface IProps {
  importDialogOpen: boolean;
  setImportDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setNewBeloppChange: (id: string | null) => void;
  isFIActive: boolean;
}

const FakturaattestImportDialog: React.FC<IProps> = props => {
  const konteringContext = useContext(KonteringContext);
  const fakturaDelContext = useContext(FakturaDelContext);
  const fakturaattestWrapperContext = useContext(FakturaattestWrapperContext);
  const formContext = useContext(BusinessDataFormContext);

  const { getKostnadsBeloppFormValue } = fakturaDelContext;

  const getKontodelByKontodel =
    konteringContext.konteringUtils.getKontodelByKontodel;
  const isKontodelAValidAttestKontodelWithoutKodbeh =
    konteringContext.konteringUtils.isKontodelAValidAttestKontodelWithoutKodbeh;
  const getActiveAttestKontodelDefsByKodplanId =
    konteringContext.konteringUtils.getActiveAttestKontodelDefsByKodplanId;

  const sysMomsTolerans = useMemo(() => {
    return fakturaattestWrapperContext.sysOptions?.find(
      sysopt => sysopt.OptionKey === LR_MOMSHANTERING_LEVFAKT_MOMSATSTOLERANS
    );
  }, [fakturaattestWrapperContext.sysOptions]);

  const kontodelDefs = useMemo(
    () =>
      getActiveAttestKontodelDefsByKodplanId(
        fakturaDelContext.kodplanId,
        fakturaDelContext.bokfDatum,
        konteringContext.attestKodbehorigheterUrval ?? [],
        formContext?.formState.currentValues?._Associations?.Faktura
          ?.IngaendeMomsBaskonto,
        fakturaDelContext.isKobFaktura
      ),
    [
      formContext?.formState.currentValues?._Associations?.Faktura
        ?.IngaendeMomsBaskonto,
      getActiveAttestKontodelDefsByKodplanId,
      konteringContext.attestKodbehorigheterUrval,
      fakturaDelContext.bokfDatum,
      fakturaDelContext.isKobFaktura,
      fakturaDelContext.kodplanId,
    ]
  );

  const isKontodelValid = useCallback(
    (
      cellValue: string,
      row: any,
      columnInfo: IInternalSelectableColumnHeader
    ) => {
      const kontodel = getKontodelByKontodel(
        fakturaDelContext.kodplanId ?? '',
        parseInt(columnInfo.associationName?.split('Kontodel')?.[1] ?? '') ?? 0,
        cellValue,
        fakturaDelContext.bokfDatum ?? new Date()
      );

      if (kontodel == null) {
        return {
          validValue: false,
          errorMessage:
            columnInfo.label + ' "' + cellValue + '" kunde inte hittas',
          errorColumn: columnInfo.associationName + '.' + columnInfo.attribute,
        };
      } else {
        const isValid = isKontodelAValidAttestKontodelWithoutKodbeh(
          kontodel,
          fakturaDelContext.bokfDatum ?? new Date(),
          formContext?.formState.currentValues?._Associations?.Faktura
            ?.IngaendeMomsBaskonto
        );
        if (!isValid) {
          return {
            validValue: false,
            errorMessage:
              columnInfo.label + ' "' + cellValue + '" är inte giltig',
            errorColumn:
              columnInfo.associationName + '.' + columnInfo.attribute,
          };
        } else {
          return {
            validValue: true,
            errorMessage: '',
            errorColumn: '',
          };
        }
      }
    },
    [
      fakturaDelContext.bokfDatum,
      fakturaDelContext.kodplanId,
      formContext?.formState.currentValues?._Associations?.Faktura
        ?.IngaendeMomsBaskonto,
      getKontodelByKontodel,
      isKontodelAValidAttestKontodelWithoutKodbeh,
    ]
  );

  const selectableColumnHeaders: ISelectableColumnHeader[] = useMemo(
    () => [
      {
        id: 'kodstrang',
        customLabel: 'Kodsträng',
        attribute: 'Kodsträng',
      },
      {
        id: 'kontodel1',
        associationName: 'Kontodel1',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'kontodel2',
        associationName: 'Kontodel2',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'kontodel3',
        associationName: 'Kontodel3',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'kontodel4',
        associationName: 'Kontodel4',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'kontodel5',
        associationName: 'Kontodel5',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'kontodel6',
        associationName: 'Kontodel6',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'kontodel7',
        associationName: 'Kontodel7',
        attribute: 'KONTODEL',
        isCellValueValid: isKontodelValid,
      },
      {
        id: 'belopp',
        attribute: 'Belopp',
        customLabel: 'Belopp',
        customColumnOptions: {
          valueFormatter: currencyValueFormatter,
        },
        isCellValueValid: isNumberCellValueValid,
      },
    ],
    [isKontodelValid]
  );

  const onCreateCallback = useCallback(
    (chosenRows: IConvertedClipboardValue[]) => {
      const isAutoAdded = true;

      const momsHantering: TEDLRLeverantorfakturaMOMSHANTERINGEnum | null =
        formContext?.parentFormContext?.formState.currentValues?._Associations
          ?.Faktura?.MOMSHANTERING;
      const registreratMomsBelopp =
        formContext?.parentFormContext?.formState.currentValues?._Associations
          ?.Faktura?.MomsBelopp2;

      const selectedMomskontodelsDefOrdningsNr = kontodelDefs.find(
        kontodel => kontodel.anvandsForMoms
      )?.ordningsNr;

      const utlandsMomsMallId =
        formContext?.parentFormContext?.formState.currentValues?._Associations
          ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms?.id;
      const utlLevMomsSats =
        formContext?.parentFormContext?.formState?.currentValues?._Associations
          ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms
          ?.UTL_MOMS_SATS;

      const startSortNumber =
        (fakturaDelContext.konteringsforslag[
          fakturaDelContext.konteringsforslag.length - 1
        ].SORTERINGS_NR ?? 0) + 1;

      // Hämta ut en default radtyp, hårdkodat momspliktigVerksamhet till false eftersom radtypen räknas om när man har markerad på koddel.
      // Om systemet istället är markerad på leverantören så spelar momspliktigVerksamhet ingen roll
      const radtypDefault = getKonteringRadtyp(
        momsHantering,
        registreratMomsBelopp,
        false
      );

      const associatedObject: FormUpdateAssociatonType[] = [];
      chosenRows.forEach((row, index) => {
        const newSortNumber = startSortNumber + index;
        const beloppValue = roundToTwoDecimals(
          parseFloat(
            currFormatter.parser.parse(row?.Belopp ?? '0').value?.toString() ??
              '0'
          )
        );

        const konteringsforslagId = `${AUTOMATICALLY_ADDED_VALUE_PREFIX}${
          index + 1
        }_${shortid.generate()}`;

        let values: INewKonteringValues = {
          id: konteringsforslagId,
          DEBET: beloppValue > 0 ? beloppValue : 0,
          KREDIT: beloppValue < 0 ? Math.abs(beloppValue) : 0,
          SORTERINGS_NR: newSortNumber,
          RADTYP: radtypDefault,

          _Associations: {
            Faktura_Ref: fakturaDelContext.fakturaId,
          },
        };

        let isKontodelMomspliktig = false;
        if (
          selectedMomskontodelsDefOrdningsNr &&
          momsHantering !== TEDLRLeverantorfakturaMOMSHANTERINGEnum.EJ_HANTERAT
        ) {
          const kalkMomssats =
            formContext?.parentFormContext?.formState?.currentValues
              ?._Associations?.Faktura?.KalkMomssats ?? 0;
          const preSelectedMomssats = fakturaattestWrapperContext.sysMoms?.find(
            sysmoms =>
              isNumberWithinMarginOfError(
                sysmoms.MOMS_SATS1,
                sysMomsTolerans?.OptionData ?? MOMSSATS_TOLERANS_DEFAULT_VALUE,
                kalkMomssats
              )
          );

          const momsKontodel = getKontodelByKontodel(
            fakturaDelContext.kodplanId ?? '',
            selectedMomskontodelsDefOrdningsNr,
            row?.collectionData?._Associations?.[
              `Kontodel${selectedMomskontodelsDefOrdningsNr}`
            ]?.KONTODEL ?? '',
            fakturaDelContext.bokfDatum ?? new Date()
          );

          const konteringsforslagsValue = getKonteringsforslagValues(
            konteringsforslagId,
            newSortNumber,
            radtypDefault,
            selectedMomskontodelsDefOrdningsNr,
            momsKontodel?.id,
            momsHantering,
            registreratMomsBelopp,
            null,
            konteringsforslagId,
            values.DEBET,
            values.KREDIT,
            beloppValue,
            beloppValue,
            null,
            undefined,
            null,
            utlLevMomsSats ?? null,
            fakturaDelContext.fakturaId,
            fakturaDelContext.kodplanId,
            fakturaDelContext.bokfDatum,
            preSelectedMomssats,
            props.setNewBeloppChange,
            getKostnadsBeloppFormValue,
            konteringContext.konteringUtils.isNewSelectedKontodelMomspliktig,
            konteringContext.konteringUtils.getAvdragsratt
          );

          values = { ...values, ...konteringsforslagsValue.values };
          associatedObject.push(...konteringsforslagsValue.associatedObject);
          isKontodelMomspliktig = values.MOMSPLIKTIG ?? false;
        }

        const radtyp = getKonteringRadtyp(
          momsHantering,
          registreratMomsBelopp,
          isKontodelMomspliktig
        );

        const calculatedValueObject = getCalculatedValueObject(
          values.RADTYP,
          values.DEBET,
          values.KREDIT,
          values.ING_MOMS_BELOPP ?? 0,
          values.UTL_MOMS ?? 0,
          1
        );

        values = { ...values, ...calculatedValueObject };

        if (radtyp === 5 || radtyp === 6) {
          values.UTL_MOMS = getUtlandskMoms(beloppValue, utlLevMomsSats);
          values.UTL_MOMS_SATS = utlLevMomsSats ?? null;
          if (utlandsMomsMallId) {
            associatedObject.push({
              associationName: 'UtlandsMomsMall',
              operation: 'Ref',
              id: utlandsMomsMallId,
              values: {
                id: utlandsMomsMallId,
              },
            });

            if (radtyp === 6) {
              values.UTL_MOMS_AVDRAG = getUtlandskAvdrag(
                beloppValue,
                utlLevMomsSats,
                1
              );
              // Vikten är alltid 1 för radtyp 6
              values.VIKT = 1;
            }
          }

          if (!isKontodelMomspliktig) {
            const updatedKostandsBeloppValue = getKostnadsBeloppFormValue(
              konteringsforslagId ?? '',
              beloppValue > 0 ? beloppValue : 0,
              beloppValue < 0 ? beloppValue : 0,
              props.setNewBeloppChange,
              radtyp,
              beloppValue,
              beloppValue,
              null,
              radtyp === 6 ? 1 : 0,
              null,
              utlLevMomsSats,
              values.UTL_MOMS
            );

            values = { ...values, ...updatedKostandsBeloppValue };
          }
        }

        fakturaDelContext.addOrUpdateKonteringsforslag(
          values,
          isAutoAdded,
          associatedObject,
          true
        );

        if (row.collectionData._Associations != null) {
          for (let i = 0; i < kontodelDefs.length; i++) {
            fakturaDelContext.setKontodel(
              values.id,
              row.collectionData._Associations?.[`Kontodel${i + 1}`]?.KONTODEL,
              i + 1,
              fakturaDelContext.kodplanId,
              fakturaDelContext.bokfDatum,
              true,
              props.isFIActive,
              true
            );
          }
        }
      });
    },
    [
      fakturaDelContext,
      fakturaattestWrapperContext.sysMoms,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?.KalkMomssats,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?.MOMSHANTERING,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?.MomsBelopp2,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms
        ?.UTL_MOMS_SATS,
      formContext?.parentFormContext?.formState.currentValues?._Associations
        ?.Faktura?._Associations?.Leverantor?._Associations?.UtlandskMoms?.id,
      getKontodelByKontodel,
      getKostnadsBeloppFormValue,
      konteringContext.konteringUtils.getAvdragsratt,
      konteringContext.konteringUtils.isNewSelectedKontodelMomspliktig,
      kontodelDefs,
      props.isFIActive,
      props.setNewBeloppChange,
      sysMomsTolerans?.OptionData,
    ]
  );

  return (
    <>
      <ImportDialog
        open={props.importDialogOpen}
        setOpen={props.setImportDialogOpen}
        collectionName={KONTERING_COLLECTION_NAME}
        invalidValuesInfoText="Orangemarkerade värden kommer ej att läsas in"
        onCreateCallback={onCreateCallback}
        selectableColumnHeaders={selectableColumnHeaders}
      />
    </>
  );
};

export default FakturaattestImportDialog;
